import { computed, reactive } from 'vue';

import { defineService } from '@nzyme/ioc/Service.js';
import { storageRef } from '@nzyme/vue-utils/reactivity/storageRef.js';
import { MARKETING_QUERY_PARAMS } from '@sigma/patient-common/landing/marketingQueryParams.js';
import type { MarketingQueryParam } from '@sigma/patient-common/landing/marketingQueryParams.js';

/**
 * The marketing params captured for a session, keyed by {@link MarketingQueryParam}.
 *
 * Partial because only the params actually present on entry are captured.
 */
export type MarketingParams = Partial<Record<MarketingQueryParam, string>>;

/**
 * Stateful, reactive store for the marketing params (utm_*, gclid, fbclid) captured for this browser session.
 *
 * Backed by sessionStorage via {@link storageRef} (browser-only). Attribution is last-touch: capturing a new
 * non-empty set overwrites the stored set, while a param-less entry leaves it intact — so the campaign survives
 * param-less internal navigations and refreshes. Used by `useLandingRoute` to re-apply `utm_campaign` to landing
 * links so the served variant survives navigating away and back, refresh, and copy-URL / open-in-new-tab.
 */
export const MarketingParamsStore = defineService({
    name: 'MarketingParamsStore',
    setup() {
        const paramsRef = storageRef<MarketingParams>({
            key: 'patient.marketing_params',
            storage: 'session',
            json: true,
            default: () => ({}),
        });

        return reactive({
            /** The marketing params captured for this session (empty object if none / storage unavailable). */
            params: computed(() => paramsRef.value ?? {}),
            capture,
            clear,
        });

        /**
         * Parse and persist the marketing params present in the given query (last-touch). No-ops when the query
         * carries no marketing params, so a param-less entry keeps the previously captured campaign intact.
         *
         * Parsing lives here (single source of truth) so callers just hand over the raw entry query.
         */
        function capture(queryString: string) {
            const searchParams = new URLSearchParams(queryString);
            const marketingParams: MarketingParams = {};
            let empty = true;
            for (const key of MARKETING_QUERY_PARAMS) {
                const value = searchParams.get(key);
                if (value !== null) {
                    marketingParams[key] = value;
                    empty = false;
                }
            }

            if (empty) {
                return;
            }

            paramsRef.value = marketingParams;
        }

        /** Clear the captured marketing params from storage. */
        function clear() {
            paramsRef.value = {};
        }
    },
});
